import { cn } from "@/utils/utils";
import { SxProps, Theme, useMediaQuery } from "@mui/material";
import { format } from "date-fns";
import { MouseEvent, ReactNode, useEffect, useState } from "react";
import {
  DateRange,
  DayPicker,
  SelectRangeEventHandler,
} from "react-day-picker";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Calendar from "@/components/ui/calendar";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  anchor?: ReactNode;
  date?: DateRange;
  handleOpenUserMenu?: (event: MouseEvent<HTMLElement>) => void;
  handleCloseUserMenu: () => void;
  handleConform: () => void;
  handleSelect: SelectRangeEventHandler;
  anchorElUser: null | HTMLElement;
  styleMenu?: SxProps<Theme>;
  classNamePlaceholder?: string;
  numberOfMonths?: number;
  contentStyle?: React.CSSProperties;
  setDateSelect: (date: DateRange) => void;
};

const SelectDate = ({
  date,
  // handleSelect,
  classNamePlaceholder,
  numberOfMonths = 2,
  contentStyle,
  setDateSelect,
  ...props
}: CalendarProps) => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>(date);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const handleDateSelect: SelectRangeEventHandler = (
    nextRange,
    selectedDay,
  ) => {
    setSelectedDate((range) => {
      if (range?.from && range?.to) return { from: selectedDay };
      return nextRange as DateRange;
    });
  };

  useEffect(() => {
    if (date?.from && date?.to) {
      setSelectedDate(date);
    } else if (!date?.from && !date?.to) {
      setSelectedDate(undefined);
    }
  }, [date]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="w-full">
        <div className="flex h-12 w-full  flex-1 cursor-pointer items-center justify-between rounded-lg bg-white px-3 pr-2 ">
          <div className="flex items-center gap-3">
            {!classNamePlaceholder && (
              <img src="/icons/calendar.svg" alt="calendar" />
            )}
            <p
              className={cn(
                "text-sm font-normal text-[#94A3B8]",
                classNamePlaceholder,
              )}
            >
              {!selectedDate?.from && !selectedDate?.to ? (
                !classNamePlaceholder && "Enter valid date range"
              ) : (
                <p className="w-full text-text">
                  {selectedDate?.from
                    ? format(selectedDate.from, "yyyy/MM/dd")
                    : " "}{" "}
                  -{" "}
                  {selectedDate?.to
                    ? format(selectedDate.to, "yyyy/MM/dd")
                    : " "}
                </p>
              )}
            </p>
          </div>
          <KeyboardArrowDownIcon
            className=""
            sx={{
              color: "#94A3B8",
            }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-full overflow-hidden"
        side="bottom"
        align={isMobile ? "end" : "start"}
        style={contentStyle}
      >
        <Calendar
          {...props}
          mode="range"
          defaultMonth={selectedDate?.from}
          numberOfMonths={numberOfMonths}
          selected={selectedDate}
          onSelect={handleDateSelect}
          initialFocus
        />
        <div className="mt-3 flex w-full justify-end gap-2 px-2">
          <button
            onClick={() => {
              setSelectedDate(undefined);
              setDateSelect({ from: undefined, to: undefined });
              setOpen(false);
            }}
            className="rounded-lg bg-button-background text-sm font-medium text-text max-md:h-8 max-md:w-[98px] lg:px-3 lg:py-2 lg:text-[16px]"
          >
            Reset Date
          </button>
          <button
            onClick={() => {
              if (selectedDate?.from && selectedDate?.to) {
                setOpen(false);
                setDateSelect(selectedDate);
              }
            }}
            disabled={!selectedDate?.from || !selectedDate?.to}
            className={cn(
              "rounded-lg bg-text text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 max-md:h-8 max-md:w-[63px] lg:px-3 lg:py-2 lg:text-[16px]",
            )}
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SelectDate;
